import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import PageTitle from "../../molecules/PageTitle";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import { formatDate } from "../../../common/const";

interface BillingInformation {
  createdAt: string;
  updatedAt: string;
  autoBillingInformationId: number;
  autoSdcId: number;
  sdcSerialNumber: string;
  buildingId: string;
  ownerId: string;
  targetSystem: string;
  registeredAt: string;
  billingStartAt: string;
  billingEndAt: string | null;
}

const BillingInformationPage = () => {
  const { autoBuildingId } = useParams();
  const [data, setData] = useState<BillingInformation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 日本時間で1ヶ月前の日付を取得
  const getOneMonthAgo = (): string => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toLocaleDateString("sv").split(" ")[0]; // 'sv' ロケールでYYYY-MM-DD形式に
  };

  // 日本時間で今日の日付を取得
  const getToday = (): string => {
    return new Date().toLocaleDateString("sv").split(" ")[0]; // 'sv' ロケールでYYYY-MM-DD形式に
  };

  const [periodFrom, setPeriodFrom] = useState(getOneMonthAgo());
  const [periodTo, setPeriodTo] = useState(getToday());
  const [dateError, setDateError] = useState<string>("");

  // 今日の日付を取得（終了日の最大値として使用）
  const today = getToday();

  // 日付の変更をハンドル
  const handleDateChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    type: "from" | "to"
  ): void => {
    if (type === "from") {
      setPeriodFrom(value);
      // 開始日が終了日より後の場合はエラー
      if (value > periodTo) {
        setDateError("開始日は終了日より前の日付を選択してください");
      } else {
        setDateError("");
      }
    } else {
      // 終了日は今日以降を選択できない
      if (value > today) {
        setDateError("終了日は今日以降の日付を選択できません");
        return;
      }
      // 終了日が開始日より前の場合はエラー
      if (value < periodFrom) {
        setDateError("終了日は開始日より後の日付を選択してください");
      } else {
        setDateError("");
      }
      setPeriodTo(value);
    }
  };

  const fetchData = async (): Promise<void> => {
    if (dateError) return;

    setIsLoading(true);
    try {
      const jwtToken = await getJwtTokenFromIdToken();
      // 日付の時刻部分を日本時間として扱う
      const fromDate = new Date(`${periodFrom}T00:00:00+09:00`).toISOString();
      const toDate = new Date(`${periodTo}T23:59:59.999+09:00`).toISOString();

      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/buildings/${autoBuildingId}/billing-information?` +
          `periodFrom=${fromDate}&periodTo=${toDate}`,
        {
          headers: {
            Authorization: jwtToken,
          },
        }
      );
      const jsonData = await response.json();
      setData(jsonData.billingInformation || []);
    } catch (error) {
      console.error("Error fetching billing information:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!dateError) {
      fetchData();
    }
  }, [autoBuildingId]);

  const handleDownloadCSV = () => {
    const header = [
      "SDCSN",
      "物件システムID",
      "LAD契約者（オーナー）ID",
      "対象システム",
      "SDC登録日時",
      "課金開始日時",
      "課金終了日時",
    ];

    const csvRows = data.map((row) => {
      let systemName;
      switch (row.targetSystem) {
        case "aal":
          systemName = "FTスマートキー";
          break;
        case "sci":
          systemName = "ミライロドア";
          break;
        case "signage":
          systemName = "サイネージ";
          break;
        case "speaker":
          systemName = "事務所SPアプリ";
          break;
        case "nfc":
          systemName = "ICカードリーダ";
          break;
        default:
          systemName = row.targetSystem;
      }

      return [
        row.sdcSerialNumber,
        row.buildingId,
        row.ownerId,
        systemName,
        new Date(row.registeredAt).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
        }),
        new Date(row.billingStartAt).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
        }),
        row.billingEndAt
          ? new Date(row.billingEndAt).toLocaleString("ja-JP", {
              timeZone: "Asia/Tokyo",
            })
          : "-",
      ];
    });

    const csvContent = [
      [
        `出力日時: ${new Date().toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
        })}`,
      ],
      [`対象期間: ${periodFrom} ～ ${periodTo}`],
      [],
      header,
      ...csvRows,
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `billing-information-${formatDate(
      new Date().toISOString()
    )}.csv`;
    link.click();
  };

  const columns: GridColDef[] = [
    {
      field: "sdcSerialNumber",
      headerName: "SDCSN",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingId",
      headerName: "物件システムID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "ownerId",
      headerName: "LAD契約者（オーナー）ID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "targetSystem",
      headerName: "対象システム",
      flex: 1,
      renderCell: (params) => {
        let value = params.value; // 最初に値を取得

        switch (value) {
          case "aal":
            value = "FTスマートキー";
            break;
          case "sci":
            value = "ミライロドア";
            break;
          case "signage":
            value = "サイネージ";
            break;
          case "speaker":
            value = "事務所SPアプリ";
            break;
          case "nfc":
            value = "ICカードリーダ";
            break;
          default:
          // 何も変更しない
        }

        return (
          <Tooltip title={value}>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },

    {
      field: "registeredAt",
      headerName: "SDC登録日時",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        const jstDate = new Date(date.getTime()).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
        return (
          <Tooltip title={jstDate}>
            <span>{jstDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "billingStartAt",
      headerName: "課金開始日時",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        const jstDate = new Date(date.getTime()).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
        return (
          <Tooltip title={jstDate}>
            <span>{jstDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "billingEndAt",
      headerName: "課金終了日時",
      flex: 1,
      renderCell: (params) => {
        const value = params.value
          ? new Date(params.value).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
          : "-";
        return (
          <Tooltip title={value}>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
  ];
  

  return (
    <CustomContainer maxWidth={false}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <PageTitle title="契約情報一覧" />
        <BackButton />
      </Box>

      <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
        <TextField
          size="small"
          label="FROM"
          type="date"
          value={periodFrom}
          onChange={(e) =>
            handleDateChange(e.target.value, setPeriodFrom, "from")
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: today,
          }}
          error={!!dateError && dateError.includes("FROM")}
          helperText={
            dateError && dateError.includes("FROM") ? dateError : ""
          }
          sx={{ minWidth: 200 }}
        />
        <Typography>～</Typography>
        <TextField
          size="small"
          label="To"
          type="date"
          value={periodTo}
          onChange={(e) => handleDateChange(e.target.value, setPeriodTo, "to")}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: today,
          }}
          error={!!dateError && dateError.includes("To")}
          helperText={
            dateError && dateError.includes("To") ? dateError : ""
          }
          sx={{ minWidth: 200 }}
        />
        に課金有効だった契約
        <Button variant="contained" onClick={fetchData} disabled={!!dateError}>
          検索
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadCSV}
          disabled={data.length === 0 || !!dateError}
        >
          CSVダウンロード
        </Button>
      </Box>

      {dateError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {dateError}
        </Alert>
      )}

      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.autoBillingInformationId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : data.length === 0
              ? customDataGridLocaleTextNoData
              : {}
          }
        />
      </div>
    </CustomContainer>
  );
};

export default BillingInformationPage;
